import type { RequestSummary } from '@Types/RequestSummary.interface';

/** Per browser tab (survives refresh); not shared across tabs like localStorage. */
export const FLOW_ANCHOR_SESSION_KEY = 'certifid-request-mfe-flow-request-id';

export function isValidRequestSummary(
    summary: RequestSummary | null | undefined,
): summary is RequestSummary {
    return summary != null && !!summary.id;
}

export function readFlowAnchorRequestIdFromSessionStorage(): string | null {
    const flowAnchorItem = sessionStorage.getItem(FLOW_ANCHOR_SESSION_KEY);
    return flowAnchorItem && flowAnchorItem.length > 0 ? flowAnchorItem : null;
}

export function writeFlowAnchorRequestIdToSessionStorage(requestId: string): void {
    sessionStorage.setItem(FLOW_ANCHOR_SESSION_KEY, requestId);
}

export function clearFlowAnchorRequestIdFromSessionStorage(): void {
    sessionStorage.removeItem(FLOW_ANCHOR_SESSION_KEY);
}

/**
 * The QR session token this browser minted for its own Match flow. Deliberately in
 * localStorage, matching where the persisted `qrSessionToken` it is compared against
 * lives: a per-tab marker would classify a second tab on the minting device as a scan.
 *
 * Write-only by design, so there is no clear helper. Nothing removes the marker: a stale
 * one cannot misclassify anything, because `isQrScanArrival` is only consulted when a
 * `qrSessionToken` is present and tokens are per-session values that never repeat. Adding
 * a clear would risk removing the marker while a token is still live, which is precisely
 * the PS-1169 misclassification.
 */
export const MINTED_QR_SESSION_TOKEN_KEY = 'certifid-request-mfe-minted-qr-session-token';

export function writeMintedQrSessionToken(token: string): void {
    localStorage.setItem(MINTED_QR_SESSION_TOKEN_KEY, token);
}

/**
 * True when this browser holds a QR session token it did not mint, which is the phone
 * that scanned the QR. RequestStartupView mints a token for whichever device starts a
 * Match and the Request BFF hands that same token back on the capture-return URL, so
 * holding one is not by itself evidence of a scan.
 */
export function isQrScanArrival(qrSessionToken: string): boolean {
    return qrSessionToken !== localStorage.getItem(MINTED_QR_SESSION_TOKEN_KEY);
}
